/* Native legacy playback; source discovery belongs to HTML, not this lifecycle guard. */
(() => {
  if (!window.customElements || customElements.get('rawry-hero-video')) return;
  const MIN_VIDEO_HEIGHT = 720;
  class RawryHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      const video = this.video;
      this.abort = new AbortController();
      const lifetime = this.abort.signal;
      this.failed = false;
      this.removeAttribute('data-has-frame');
      this.removeAttribute('data-playback');
      this.inView = this.getBoundingClientRect().bottom > 0 && this.getBoundingClientRect().top < innerHeight;
      video.muted = true;
      const on = (target, event, handler) => target.addEventListener(event, handler, {signal: lifetime});
      // This muted hero has no pause control. Browser pause events do not imply user intent.
      on(document, 'visibilitychange', () => this.sync());
      const showFrame = () => {
        if (!this.wantsPlay()) { this.pause(); return; }
        const reveal = () => {
          if (lifetime.aborted) return;
          this.frameRequest = null;
          if (!this.wantsPlay()) return;
          if (video.videoHeight < MIN_VIDEO_HEIGHT) { this.still(); return; }
          this.setAttribute('data-has-frame', '');
        };
        if (this.frameRequest != null) video.cancelVideoFrameCallback(this.frameRequest);
        if (video.requestVideoFrameCallback) this.frameRequest = video.requestVideoFrameCallback(reveal);
        else reveal();
      };
      on(video, 'playing', showFrame);
      on(video, 'error', () => this.still());
      video.querySelectorAll('source').forEach(source => on(source, 'error', () => this.still()));
      on(document, 'shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      });
      on(document, 'shopify:section:load', () => this.sync());
      this.observer = new IntersectionObserver(entries => {
        if (lifetime.aborted) return;
        for (let index = entries.length - 1; index >= 0; index--) {
          if (entries[index].target !== this) continue;
          this.inView = entries[index].isIntersecting;
          this.sync();
          break;
        }
      });
      this.observer.observe(this);
      // Reconnection restores only this video's original markup source, never a fallback path.
      if (this.sources) {
        this.sources.forEach(({element, src}) => element.setAttribute('src', src));
        this.sources = null;
        video.load();
      }
      if (video.error) this.still();
      else {
        this.sync();
        if (!video.paused && video.readyState >= 2) showFrame();
      }
    }
    pause() {
      if (!this.video.paused) this.video.pause();
    }
    wantsPlay() {
      return this.abort && !this.failed && this.inView && !document.hidden;
    }
    sync() {
      if (!this.abort) return;
      if (!this.wantsPlay()) { this.pause(); return; }
      const lifetime = this.abort.signal;
      this.video.play().catch(error => {
        if (lifetime.aborted || error.name === 'AbortError') return;
        this.still();
      });
    }
    still() {
      this.failed = true;
      this.removeAttribute('data-has-frame');
      this.setAttribute('data-playback', 'still');
      this.video.pause();
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.abort = null;
      this.observer.disconnect();
      if (this.frameRequest != null) this.video.cancelVideoFrameCallback(this.frameRequest);
      this.frameRequest = null;
      this.video.pause();
      this.removeAttribute('data-has-frame');
      this.sources = Array.from(this.video.querySelectorAll('source[src]'), element => ({element, src: element.getAttribute('src')}));
      this.sources.forEach(({element}) => element.removeAttribute('src'));
      this.video.load();
    }
  }
  customElements.define('rawry-hero-video', RawryHeroVideo);
})();
